// src/components/AgreementDocument.js - task list section 764 (ruling 324 step 3). One agreement, rule by rule: the notice that
// it is not confirmed until its General Chairman signs off or replaces it, its source, a table of contents, every rule with
// its page in the source and what the word-for-word comparison found, every dated change under the rule it changes, and every
// gap named. "Copy reference" gives the member the citation to put in a claim or a letter. Data: src\data\agreementDocument.js.
import React, { useState } from 'react';
import { citation } from '../data/agreementDocument';

const C = { text: '#D6E4F0', dim: '#8AA4BF', line: '#2E4A6B', bg: '#0A1628', warn: '#FFD27A', warnLine: '#8A6A1F', bad: '#F2A0A0', ok: '#9FD8A8' };
const box = { border: '1px solid ' + C.line, background: C.bg, borderRadius: 6, padding: '8px 12px', marginBottom: 12 };

export default function AgreementDocument({ doc }) {
  const [copied, setCopied] = useState('');
  const [showReview, setShowReview] = useState(false);
  if (!doc) return null;

  const jump = (anchor) => {
    try { const el = document.getElementById(anchor); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { /* nothing to scroll */ }
  };
  const copy = async (entry) => {
    const ref = citation(doc, entry);
    try { await navigator.clipboard.writeText(ref); setCopied(entry.anchor); } catch (e) { setCopied('fail:' + ref); }
  };
  const resultColor = (r) => (r === 'MATCHES' ? C.ok : r === 'DIFFERS' ? C.bad : r === 'SOURCE' ? C.warn : C.dim);

  return (
    <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>
      <div style={{ fontSize: 12, color: C.warn, border: '1px solid ' + C.warnLine, borderRadius: 6, padding: '6px 10px', marginBottom: 12 }}>{doc.unconfirmed}</div>

      <div style={box}>
        <div style={{ fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Source</div>
        {doc.sources.map((s, i) => (
          <div key={i} style={{ fontSize: 12 }}>{s.file}{s.note ? <span style={{ color: C.dim }}> - {s.note}</span> : null}</div>
        ))}
        <div style={{ fontSize: 12, color: C.dim, marginTop: 4 }}>{doc.sourceKindText}</div>
        {doc.pageNote && <div style={{ fontSize: 12, color: C.dim }}>Pages: {doc.pageNote}</div>}
        {doc.comparedOn && <div style={{ fontSize: 12, color: C.dim }}>Compared word for word with the source file on {doc.comparedOn}.</div>}
      </div>

      {doc.gaps.length > 0 && (
        <div style={{ ...box, borderColor: C.warnLine }}>
          <div style={{ fontSize: 11, color: C.warn, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Not on the platform ({doc.gaps.length})</div>
          {doc.gaps.map((g, i) => (
            <div key={i} style={{ fontSize: 12, marginBottom: 6 }}>
              {g.what}{g.page && g.page !== 'n/d' ? ' Source: ' + g.page + '.' : ''}
              {g.where && <div style={{ color: C.dim, fontSize: 11 }}>{g.where}</div>}
              {g.sourceText && (
                <details style={{ marginTop: 4 }}>
                  <summary style={{ fontSize: 12, color: '#A8C4DF', cursor: 'pointer' }}>What the source file has for it</summary>
                  <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>{g.sourceFile}{g.sourceLines ? ', ' + g.sourceLines : ''}. Shown as the file has it. Not part of the claim tools yet.{g.sourceNote ? ' ' + g.sourceNote : ''}</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 4 }}>{g.sourceText}</div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {doc.toc.length > 0 && (
        <div style={box}>
          <div style={{ fontSize: 11, color: C.dim, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Contents ({doc.toc.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {doc.toc.map((t) => (
              <button key={t.anchor} type="button" onClick={() => jump(t.anchor)}
                style={{ background: 'none', border: 'none', color: '#A8C4DF', textAlign: 'left', cursor: 'pointer', padding: '2px 0', fontSize: 12 }}>{t.label}</button>
            ))}
          </div>
        </div>
      )}

      {doc.entries.map((e) => (
        <div key={e.anchor} id={e.anchor} style={{ borderTop: '1px solid ' + C.line, paddingTop: 10, marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700 }}>{e.label}{e.title ? ' - ' + e.title : ''}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: C.dim }}>{e.page && e.page !== 'n/d' ? e.page : 'page not determined'}</span>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(e)}>{copied === e.anchor ? 'Copied' : 'Copy reference'}</button>
            </div>
          </div>
          {copied === 'fail:' + citation(doc, e) && <div style={{ fontSize: 11, color: C.dim }}>Copy this reference: {citation(doc, e)}</div>}
          <div style={{ fontSize: 11, color: resultColor(e.check.result), margin: '2px 0 6px' }}>{e.check.label}{e.check.note ? ' ' + e.check.note : ''}</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{e.text || '(no text on the platform)'}</div>
          {e.baseText && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ fontSize: 12, color: C.dim, cursor: 'pointer' }}>The wording before the later modifications</summary>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: C.dim, marginTop: 4 }}>{e.baseText}</div>
              {e.inForce && <div style={{ fontSize: 11, color: resultColor(e.inForce.result), marginTop: 4 }}>Wording in force: {e.inForce.label}{e.inForce.note ? ' ' + e.inForce.note : ''}</div>}
            </details>
          )}
          {e.layers.map((l, i) => (
            <div key={i} style={{ ...box, marginTop: 8, marginBottom: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{l.heading}</div>
              <div style={{ fontSize: 11, color: C.dim }}>{l.where}{l.page && l.page !== 'n/d' ? ' Source: ' + l.page + '.' : ''}</div>
              {l.notes && <div style={{ fontSize: 11, color: C.dim }}>{l.notes}</div>}
              {l.check && <div style={{ fontSize: 11, color: resultColor(l.check.result) }}>{l.check.label}{l.check.note ? ' ' + l.check.note : ''}</div>}
              {l.text && <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{l.text}</div>}
              {l.textMissing && !l.sourceText && <div style={{ fontSize: 12, color: C.warn, marginTop: 4 }}>The changed wording is not on the platform.</div>}
              {l.sourceText && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 12, color: C.warn }}>Not part of the claim tools yet. The source file ({l.sourceFile}) has:{l.sourceNote ? ' ' + l.sourceNote : ''}</div>
                  <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{l.sourceText}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {(doc.recordGaps.length > 0 || doc.reviewNotes.length > 0) && (
        <div style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowReview(!showReview)}>{showReview ? 'Hide' : 'Show'} the review notes for the General Chairman</button>
          {showReview && (
            <div style={{ ...box, marginTop: 8 }}>
              {doc.recordGaps.concat(doc.reviewNotes).map((n, i) => <div key={i} style={{ fontSize: 12, color: C.dim, marginBottom: 4 }}>{n}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
